import { ref, type Ref } from 'vue'
import { useEmitter } from '../emitter'
import { useReadyTrigger } from '@booksy-com/vue-tools'

export interface ModalHandlers {
  onOpen: (options?: unknown) => void
  onClose: () => void
  onCancel: () => void
}

export interface UseModalReturn {
  open: (options?: unknown) => Promise<void>
  close: () => Promise<void>
  cancel: () => Promise<void>
  isOpen: Ref<boolean>
  onCreatedHandler: (handlers: ModalHandlers) => void
  onBeforeDestroyHandler: (handlers: ModalHandlers) => void
}

const readyTrigger = useReadyTrigger()

const isOpen: Ref<boolean> = ref(false)

const onCreatedHandler = (handlers: ModalHandlers): void => {
  const { on, EVENTS } = useEmitter()

  on(EVENTS.MODAL_OPEN, handlers.onOpen)
  on(EVENTS.MODAL_CLOSE, handlers.onClose)
  on(EVENTS.MODAL_CANCEL, handlers.onCancel)
  readyTrigger.resolveTrigger()
}

const onBeforeDestroyHandler = (handlers: ModalHandlers): void => {
  const { off, EVENTS } = useEmitter()

  off(EVENTS.MODAL_OPEN, handlers.onOpen)
  off(EVENTS.MODAL_CLOSE, handlers.onClose)
  off(EVENTS.MODAL_CANCEL, handlers.onCancel)
}

const _modalAction = (action: () => void): Promise<void> => {
  return readyTrigger.promise.then(() => {
    return action()
  })
}

const open = async (options?: unknown): Promise<void> => {
  return _modalAction(() => {
    const { emit, EVENTS } = useEmitter()

    isOpen.value = true

    emit(EVENTS.MODAL_OPEN, options)
  })
}

const close = async (): Promise<void> => {
  return _modalAction(() => {
    const { emit, EVENTS } = useEmitter()

    isOpen.value = false

    emit(EVENTS.MODAL_CLOSE)
  })
}

const cancel = async (): Promise<void> => {
  return _modalAction(() => {
    const { emit, EVENTS } = useEmitter()

    isOpen.value = false

    emit(EVENTS.MODAL_CANCEL)
  })
}

export function useModal (): UseModalReturn {
  return { open, close, cancel, isOpen, onCreatedHandler, onBeforeDestroyHandler }
}
